// Single source of truth for the primary navigation drawer (LIQ-2643).
//
// Structure, labels and order follow Product's sign-off on LIQ-2643 (Home, Upload, Data,
// Reconciliation, Cash Call, Export, Analytics, Administration, Superuser), with My Account, Help
// and Support and Sign Out pinned to the bottom of the rail. Only items with a page or link today
// are listed - Non-FCP/Cashbook Reconciliation, Co-Lead Cash Call and the Superuser
// group are added once Product names their destinations. Data Explorer, Data Submission and the
// Analytics items open the matching report from the user's My Reports, and are hidden for users
// who don't have it. FCP Logs, Manage
// Reports, Invoices needing attention and the Current Report actions are kept in the nearest
// group so every previous menu destination stays reachable.
//
// Each item: { key, label, icon, route?, action?, href?, external?, children?, match?,
//              visible?, disabled?, report?, badge?, animate?, noActive?, bottom? }
//  - route    : vue-router location; the leaf navigates here. A route with a query or params is
//               only active while the current route carries those same values.
//  - action   : emitted to the host (AppNavbar) when the item is clicked; with
//               no route the item is a plain button (e.g. upload, fullscreen)
//  - href     : plain link (mailto: or a website); external opens it in a new tab
//  - match    : extra route names that should mark this item (or group) active
//  - visible  : (ctx) => boolean; items the user cannot access are not rendered
//  - disabled : (ctx) => boolean; rendered but not clickable
//  - report   : opens the user's My Reports entry with this name (see linkMyReports); hidden
//               when they have no such report
//  - badge    : (ctx) => boolean; shows a warning marker beside the label
//  - animate  : (ctx) => boolean; spins the item's icon (busy state)
//  - noActive : never highlighted as the current page (shares a route name)
//  - bottom   : pinned to the bottom of the rail, below a divider, with Sign Out after it
// ctx = { isSuperuser, isCompany, hasFCPAccess, canAccessCashCall, exportEnabled }
//       plus the host-supplied flags used below: newBdxItems, canUploadFcp, hasDataValidation,
//       invoiceNeedsAttention, reportActions, reportLoading, dedupeBusy, and myReports (the
//       user's My Reports list, for linkMyReports).

export const SUPPORT_EMAIL = 'support@limeiq.com'
export const THE_Q_URL = 'https://portal.limeiq.com'

// The menu groups shown as tiles on the home page, in Product's order. A group the user can't
// see has no tile.
export const HOME_TILE_KEYS = ['upload', 'data', 'reconciliation', 'cash-call', 'export', 'analytics']

export const NAV_MENU = [
  {
    // The home page with the section tiles (/home, pages/HomePage.vue).
    key: 'home',
    label: 'Home',
    icon: 'house',
    route: { name: 'HomePage' }
  },
  {
    key: 'upload',
    label: 'Upload',
    icon: 'plus-circle',
    children: [
      { key: 'upload-data', label: 'Data', action: 'upload-bdx' },
      {
        key: 'upload-cashbook',
        label: 'Cashbook',
        action: 'upload-fcp',
        visible: (ctx) => ctx.canUploadFcp
      }
    ]
  },
  {
    key: 'data',
    label: 'Data',
    icon: 'list-ul',
    children: [
      {
        key: 'data-list',
        label: 'Data List',
        route: { name: 'ManageBDX' },
        badge: (ctx) => ctx.newBdxItems
      },
      {
        // The native BDX Validation report - assigned per company like any report.
        key: 'data-validation',
        label: 'Data Validation',
        route: { name: 'BDXValidationReport' },
        visible: (ctx) => ctx.isSuperuser || ctx.hasDataValidation
      },
      {
        // Power BI reports from the user's My Reports (see linkMyReports) - both open the same one.
        key: 'data-explorer',
        label: 'Data Explorer',
        report: 'BDX Explorer v3'
      },
      {
        key: 'data-submission',
        label: 'Data Submission',
        report: 'BDX Explorer v3'
      },
      {
        key: 'slip-share',
        label: 'Slip Share',
        route: { name: 'SlipShareService' },
        visible: (ctx) => ctx.isSuperuser
      },
      {
        key: 'orphan-claims',
        label: 'Orphan Claims',
        route: { name: 'OrphanClaims' }
      },
      {
        key: 'refresh-data',
        label: 'Refresh Data',
        action: 'dedupe',
        visible: (ctx) => ctx.isSuperuser,
        animate: (ctx) => ctx.dedupeBusy
      }
    ]
  },
  {
    key: 'reconciliation',
    label: 'Reconciliation',
    icon: 'arrow-repeat',
    visible: (ctx) => ctx.hasFCPAccess,
    children: [
      {
        key: 'fcp-reconciliation',
        label: 'FCP Reconciliation',
        route: { name: 'Reconciliation' }
      },
      {
        key: 'fcp-logs',
        label: 'FCP Logs',
        route: { name: 'FCPLogList' },
        match: ['FCPLogPreview']
      }
    ]
  },
  {
    key: 'cash-call',
    label: 'Cash Call',
    icon: 'currency-dollar',
    // Matches the CashCallDetails route guard, which rejects superusers
    // without a company membership.
    visible: (ctx) => ctx.canAccessCashCall,
    children: [
      {
        key: 'cash-call-details',
        label: 'Cash Call',
        route: { name: 'CashCallDetails' }
      }
    ]
  },
  {
    key: 'export',
    label: 'Export',
    icon: 'box-arrow-up',
    // The Export page itself (e.g. amending a saved report from the Export Log).
    match: ['Export'],
    children: [
      {
        // ?start= opens that flow of the Export wizard (see SaveReportWizard's mounted()).
        key: 'full-data-export',
        label: 'Full Data Export',
        route: { name: 'Export', query: { start: 'full-export' } },
        visible: (ctx) => ctx.exportEnabled
      },
      {
        key: 'new-report',
        label: 'New Report',
        route: { name: 'Export', query: { start: 'new-report' } },
        visible: (ctx) => ctx.exportEnabled
      },
      { key: 'export-log', label: 'Export Log', route: { name: 'ExportLog' } }
    ]
  },
  {
    // Product's six Analytics items, alphabetical. Each opens a report from the user's My Reports
    // (see linkMyReports) and is hidden without it. The claim items share the "Claim Reports"
    // report and the program items "Program Analysis (3.22.26)".
    key: 'analytics',
    label: 'Analytics',
    icon: 'bar-chart',
    children: [
      { key: 'claim-analysis', label: 'Claim Analysis', report: 'Claim Reports' },
      { key: 'fee-report', label: 'Fee Report', report: 'Program Analysis (3.22.26)' },
      { key: 'inventory-report', label: 'Inventory Report', report: 'Program Analysis (3.22.26)' },
      { key: 'lifecycle-report', label: 'Lifecycle Report', report: 'Program Analysis (3.22.26)' },
      { key: 'paid-analysis', label: 'Paid Analysis', report: 'Claim Reports' },
      { key: 'reserve-analysis', label: 'Reserve Analysis', report: 'Claim Reports' }
    ]
  },
  {
    // Only while a report is on screen (Dashboard / ReportView).
    key: 'current-report',
    label: 'Current Report',
    icon: 'graph-up',
    visible: (ctx) => ctx.reportActions,
    children: [
      {
        key: 'report-fullscreen',
        label: 'Full Screen',
        action: 'fullscreen',
        disabled: (ctx) => ctx.reportLoading
      },
      {
        key: 'report-share',
        label: 'Share',
        action: 'share',
        disabled: (ctx) => ctx.reportLoading
      }
    ]
  },
  {
    key: 'administration',
    label: 'Administration',
    icon: 'gear',
    visible: (ctx) => ctx.isSuperuser || ctx.isCompany,
    children: [
      {
        key: 'company',
        label: 'Company',
        route: { name: 'Companies' },
        match: ['ViewCompany', 'ManageCompanyProfile', 'ManageCompanyUsers',
          'ManageCompanyReports', 'ManageCompanyRLS', 'ManageCompanyCashCall',
          'ManageCompanyBilling'],
        visible: (ctx) => ctx.isSuperuser
      },
      { key: 'users', label: 'Users', route: { name: 'Users' } },
      {
        key: 'invoicing',
        label: 'Invoicing',
        route: { name: 'Invoicing' },
        visible: (ctx) => ctx.isSuperuser
      },
      {
        key: 'invoice-attention',
        label: 'Invoices Needing Attention',
        route: {
          name: 'Invoicing',
          query: { tab: 'generate-invoices', filter: 'needs-attention' }
        },
        action: 'invoice-attention',
        noActive: true,
        badge: () => true,
        visible: (ctx) => ctx.isSuperuser && ctx.invoiceNeedsAttention
      },
      { key: 'manage-reports', label: 'Manage Reports', route: { name: 'Reports' } }
    ]
  },
  {
    key: 'account',
    label: 'My Account',
    icon: 'person-circle',
    bottom: true,
    children: [
      { key: 'profile', label: 'Profile', route: { name: 'UserProfile' } },
      // The Settings page - the change password form, plus two-factor settings.
      { key: 'change-password', label: 'Change Password', route: { name: 'UserSettings' } }
    ]
  },
  {
    key: 'help',
    label: 'Help and Support',
    icon: 'question-circle',
    bottom: true,
    children: [
      { key: 'email-support', label: 'Email Support', href: `mailto:${SUPPORT_EMAIL}` },
      {
        key: 'website',
        label: 'Visit Our Website',
        href: 'https://www.limeiq.com',
        external: true
      },
      {
        // The Q Portal - LimeIQ's resource hub, whose menu this one mirrors (LIQ-2765).
        key: 'the-q',
        label: 'Go to The Q',
        href: THE_Q_URL,
        external: true
      }
    ]
  }
]

const isVisible = (item, ctx) => !item.visible || item.visible(ctx)

// Shown but not clickable (e.g. Current Report's actions while the report loads).
export const isItemDisabled = (item, ctx) => !!item.disabled && item.disabled(ctx)

// Returns a copy of the menu with inaccessible items removed. A group whose
// children are all hidden is dropped too, so no empty parents are rendered.
export const filterMenu = (items, ctx) =>
  items.reduce((acc, item) => {
    if (!isVisible(item, ctx)) return acc

    if (item.children) {
      const children = filterMenu(item.children, ctx)
      if (!children.length) return acc
      acc.push({ ...item, children })
    } else {
      acc.push(item)
    }

    return acc
  }, [])

const normaliseName = (name) => String(name || '').trim().replace(/\s+/g, ' ').toLowerCase()

// Points each `report` item at the user's My Reports entry with that name (its display name or
// its Power BI name, ignoring case and spacing) - the same report, opened the same way, as picking
// it from My Reports. One the user doesn't have there is hidden, like any item they can't access
// (filterMenu then drops a group left empty). Several items can share a report, so ?view= carries
// the item's key: the report ignores it, but it lets only the item that was picked show as the
// current page.
export const linkMyReports = (items, myReports, encodeReportID) =>
  items.map((item) => {
    if (item.children) {
      return { ...item, children: linkMyReports(item.children, myReports, encodeReportID) }
    }
    if (!item.report) return item

    const wanted = normaliseName(item.report)
    const match = (myReports || []).find((report) =>
      [report.display_name, report.name].some((name) => normaliseName(name) === wanted)
    )

    return match
      ? {
          ...item,
          route: {
            name: 'Dashboard',
            params: { report_id: encodeReportID(match.report_id || match.id) },
            query: { view: item.key }
          }
        }
      : { ...item, visible: () => false }
  })

// Every key the item's route sets (query or params) must hold the same value on the current route.
const valuesMatch = (expected, actual) =>
  !expected || Object.keys(expected).every((key) => String(expected[key]) === String((actual || {})[key]))

// current = the current route's { name, query, params }.
export const isItemActive = (item, current) =>
  !!item.route &&
  !item.noActive &&
  (item.route.name === current.name || (item.match || []).includes(current.name)) &&
  valuesMatch(item.route.query, current.query) &&
  valuesMatch(item.route.params, current.params)

export const isGroupActive = (item, current) =>
  !!item.children &&
  ((item.match || []).includes(current.name) || item.children.some((c) => isItemActive(c, current)))

// Key of the group that contains the current page, or null.
export const findActiveGroupKey = (items, current) => {
  const group = items.find((i) => isGroupActive(i, current))
  return group ? group.key : null
}

export const isHamburgerMenuEnabled = () =>
  ['1', 'true', 'True'].includes(process.env.VUE_APP_ENABLE_HAMBURGER_MENU)
